import React, { UIEvent, useEffect, useRef, useState } from 'react';
import { Location, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { logEvent } from 'firebase/analytics';
import { NumberParam, useQueryParam } from 'use-query-params';

import {
  getFilteredInspirationsBySearchTerm,
  getInspirationsByType,
  getNextHolidayTheme,
  getThemeLabel,
  InspirationCategories,
  InspirationIdea,
  InspirationMainCategoryDefinition,
  InspirationSubCategory,
  InspirationSubCategoryDefinition,
  InspirationTheme,
  InspirationThemeDefinition,
  sortedHolidayDefinitions
} from '../../lib/inspiration';
import SideBar from './SideBar';
import BasePageHeader, { UPageType } from '../BasePageHeader';
import { BasePageContent } from '../BasePage';
import SearchBox from './SearchBox';

import Sparkles from '../../components/icons/Sparkles';
import { useUserContext } from '../../contexts/UserContext';
import { ColorScheme, useBackgroundContext } from '../../contexts/BackgroundContext';
import { firebaseAnalytics } from '../../core/libs/Firebase';
import AdjustmentsVertical from '../../components/icons/AdjustmentsVertical';

import { DeviceQueries, VerticalFullContainer } from 'Common/src/components/styled';
import { logSumoEvent, ULogApplication, ULogSeverity, ULogTag } from 'Common/src/api/SumoLogicApi';
import { DesktopShow, MobileShow } from '../../components/styled/Common';

const INVITE_WIDTH = 311;
const INVITE_GAP = 24;
const INVITE_MOBILE_WIDTH = 115;
const INVITE_MOBILE_GAP = 8;

const InstantInvitesPage = () => {
  const navigate = useNavigate();
  const location = useLocation() as Location<{ theme?: InspirationTheme }>;
  const userContext = useUserContext();
  const backgroundContext = useBackgroundContext();

  // TODO: better way to determine default category (based on time of year)
  const [theme, setTheme] = useState<InspirationTheme | undefined>(getNextHolidayTheme().id);
  const [searchQuery, setSearchQuery] = useState('');
  const [inspirationsByType, setInspirationsByType] = useState<Map<InspirationTheme, InspirationIdea[]>>(new Map());
  const [categoriesShown, setCategoriesShown] = useState(false);

  const categoryContainerRef = useRef<HTMLDivElement>(null);
  const createDivRef = useRef<HTMLDivElement>(null);
  const themeContainers = useRef(new Map<InspirationTheme, (HTMLDivElement | null)>());
  const themeImageContainers = useRef(new Map<InspirationTheme, (HTMLDivElement | null)>());
  const lastCalculatedScroll = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [themeParam] = useQueryParam('theme', NumberParam);

  useEffect(() => {
    document.title = 'Shine Parties - Instant Invites';
    logEvent(firebaseAnalytics, 'page_view', {
      page_title: 'Instant Invites - Analytics'
    });

    backgroundContext.setScheme({ scheme: ColorScheme.Welcome });

    void fetchInspirationsByType();
  }, []);

  useEffect(() => {
    if (themeParam !== undefined) {
      setTimeout(() => {
        onThemeClick(themeParam as InspirationTheme);
      }, 0);
    }
  }, [themeParam]);

  useEffect(() => {
    const positionCreate = () => {
      setTimeout(() => {
        if (categoryContainerRef.current && createDivRef.current) {
          let pos;
          if (window.innerWidth > 767) {
            pos = ((Math.floor(
              (categoryContainerRef.current.clientWidth + INVITE_GAP) / (INVITE_WIDTH + INVITE_GAP)) - 1) * 2) - 1;
          } else {
            pos = ((Math.floor(
              (categoryContainerRef.current.clientWidth + INVITE_MOBILE_GAP) / (INVITE_MOBILE_WIDTH + INVITE_MOBILE_GAP)) - 1) * 2) - 1;
          }

          createDivRef.current.style.order = `${pos}`;
        }
      }, 0);
    };

    window.addEventListener('resize', positionCreate);

    positionCreate();

    return () => window.removeEventListener('resize', positionCreate);
  }, [categoryContainerRef.current, createDivRef.current, inspirationsByType]);

  useEffect(() => {
    setTimeout(() => {
      if (location.state?.theme && location.state.theme !== theme) {
        scrollToTheme(location.state.theme);
        setTheme(location.state.theme);
      }
    }, 0);
  }, [location.state]);

  const fetchInspirationsByType = async () => {
    setInspirationsByType(
      await getInspirationsByType()
    );
  };

  const onThemeChange = (theme?: InspirationTheme) => {
    setTheme(theme);
    navigate(
      '/instant',
      {
        replace: true,
        state: {
          theme
        }
      });
  };

  const scrollToTheme = (theme: InspirationTheme) => {
    const offset = (containerRef.current?.querySelector(SubHeader.toString()) as HTMLDivElement)?.offsetTop ?? 0;
    const top = (themeContainers.current?.get(theme)?.offsetTop ?? 0) - offset;

    containerRef.current?.scroll({ top });
  };

  const onThemeClick = (theme: InspirationTheme) => {
    onThemeChange(theme);
    setCategoriesShown(false);
    scrollToTheme(theme);
  };

  const onSearch = async () => {
    setInspirationsByType(
      await getFilteredInspirationsBySearchTerm(searchQuery)
    );
    onThemeChange(undefined);
  };

  const inspire = (inspiration: InspirationIdea) => {
    void logSumoEvent({
      app: ULogApplication.ELK,
      severity: ULogSeverity.INFO,
      userId: userContext.id,
      tag: ULogTag.UserAction,
      message: `[InstantInvitePage] Clicked on instant invite #${inspiration.id}`
    });

    navigate(`/event/create/fromGallery/${inspiration.id}`);
  };

  const create = () => {
    void logSumoEvent({
      app: ULogApplication.ELK,
      severity: ULogSeverity.INFO,
      userId: userContext.id,
      tag: ULogTag.UserAction,
      message: '[InstantInvitePage] Clicked on "create your own"'
    });

    navigate('/event/create');
  };

  const onScroll = (e: UIEvent) => {
    const now = Date.now();
    if (now - lastCalculatedScroll.current < 100) {
      return;
    }
    lastCalculatedScroll.current = now;

    const scrollTop = e.currentTarget?.scrollTop;
    if (scrollTop === undefined) {
      return;
    }

    for (const cat of inspirationTypes) {
      const imageContainer = themeImageContainers.current.get(cat);
      if (imageContainer && (imageContainer.offsetTop + imageContainer.clientHeight - (imageContainer.children?.[0]?.clientHeight ?? 0)) > scrollTop) {
        onThemeChange(cat);
        return;
      }
    }
  };

  const filteredMainCategories: InspirationMainCategoryDefinition[] = [];
  const inspirationTypes: InspirationTheme[] = [];
  for (const mainCat of InspirationCategories) {
    const subCategories: InspirationSubCategoryDefinition[] = [];
    for (const subCat of mainCat.subCategories) {
      const themes: InspirationThemeDefinition[] = [];
      let themeDefinitions = subCat.themes;
      if (subCat.id === InspirationSubCategory.Holidays) {
        themeDefinitions = sortedHolidayDefinitions(themeDefinitions);
      }
      for (const theme of themeDefinitions) {
        if (inspirationsByType.has(theme.id)) {
          themes.push(theme);
          inspirationTypes.push(theme.id);
        }
      }
      if (themes.length > 0) {
        subCategories.push({...subCat, themes })
      }
    }
    if (subCategories.length > 0) {
      filteredMainCategories.push({...mainCat, subCategories });
    }
  }

  return <PageLayout>
    <SideBar mainCategories={filteredMainCategories} theme={theme} onThemeClick={onThemeClick}
             onCreateClick={create} mobileShown={categoriesShown} onClose={() => setCategoriesShown(false)}/>
    <VerticalFullContainer>
      <BasePageHeader page={UPageType.Gallery}/>
      <BasePageContent $wide={true} $full={true} $relative={false} $flush={true} onScroll={onScroll}
                       containerRef={containerRef}>
        <StickyHeader>
          <Header>
            Instant Invites
          </Header>

          <SearchBox value={searchQuery} onChange={setSearchQuery} onSearch={onSearch}/>

          <CategoriesSwitch onClick={() => setCategoriesShown(true)}>
            <AdjustmentsVertical size={24}/>
            Categories
          </CategoriesSwitch>
        </StickyHeader>
        <PageContent>

          <CategoriesContainer ref={categoryContainerRef}>
            {
              inspirationTypes.map((theme, index) => {
                return <div key={theme} ref={(div) => themeContainers.current.set(theme, div)}>
                  <SubHeader>
                    {getThemeLabel(theme)}
                  </SubHeader>
                  <InspirationGrid ref={(div) => themeImageContainers.current.set(theme, div)}>
                    {
                      index === 0 &&
                      <CreateYourOwn ref={createDivRef} onClick={create}>
                        <DesktopShow><Sparkles size={80}/></DesktopShow>
                        <MobileShow><Sparkles size={28}/></MobileShow>
                        Create your own
                      </CreateYourOwn>
                    }
                    {
                      inspirationsByType.get(theme)?.map((inspiration, index) => <Inspiration
                        key={inspiration.id} onClick={() => inspire(inspiration)} style={{ order: index * 2 }}>
                        <InspirationImage src={inspiration.photoUrl} width={INVITE_WIDTH} height={INVITE_WIDTH}/>
                        <Creator><Sparkles/>{inspiration.creator ?? 'Shine'}</Creator>
                      </Inspiration>)
                    }
                  </InspirationGrid>
                </div>;
              })
            }
            {
              inspirationTypes.length === 0 && searchQuery !== '' && <div>
                No matching results found.
              </div>
            }
          </CategoriesContainer>
        </PageContent>
      </BasePageContent>
    </VerticalFullContainer>
  </PageLayout>;
};

const PageLayout = styled(VerticalFullContainer)`
  width: 100%;
  flex-direction: row;

  @media (${DeviceQueries.mobile}) {
    flex-direction: column-reverse;
  }
`;

const PageContent = styled.div`
  padding: 0 40px 0 60px;

  @media (${DeviceQueries.mobile}) {
    padding: 0 16px;
  }
`;

const Header = styled.h1`
  margin: 0;
  color: var(--sp-text-color);
  font-size: 48px;
  font-weight: 600;

  @media (${DeviceQueries.mobile}) {
    font-size: 28px;
  }
`;

const StickyHeader = styled(PageContent)`
  position: sticky;
  top: 0;
  background: var(--sp-bg-color);
  padding-bottom: 10px;
`;

const SubHeader = styled.h2`
  color: var(--sp-text-color);
  font-size: 40px;
  font-weight: 600;
  margin: 0;

  @media (${DeviceQueries.mobile}) {
    font-size: 18px;
  }
`;

const InspirationGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${INVITE_GAP}px;
  margin: 24px 0;

  @media (${DeviceQueries.mobile}) {
    gap: ${INVITE_MOBILE_GAP}px;
  }
`;

const CreateYourOwn = styled.div`
  width: ${INVITE_WIDTH}px;
  height: ${INVITE_WIDTH}px;

  border-radius: 18px;
  border: 2px solid var(--sp-border-color);
  background: var(--sp-module-bg-color);
  color: var(--sp-text-color);
  text-align: center;
  font-size: 48px;
  font-weight: 500;
  line-height: 50px;
  cursor: pointer;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 21px;
  padding: 24px;
  box-sizing: border-box;

  /* Shadows/Large */
  box-shadow: 0 5px 15px 0 rgba(0, 0, 0, 0.12), 0 15px 35px 0 rgba(103, 110, 118, 0.08);

  @media (${DeviceQueries.mobile}) {
    width: ${INVITE_MOBILE_WIDTH}px;
    height: ${INVITE_MOBILE_WIDTH}px;
    padding: 15px;
    font-size: 16px;
    line-height: 17px;
  }
`;

const Inspiration = styled.div`
  width: ${INVITE_WIDTH}px;
  height: ${INVITE_WIDTH + 25}px;
  margin: 0;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media (${DeviceQueries.mobile}) {
    width: ${INVITE_MOBILE_WIDTH}px;
    height: ${INVITE_MOBILE_WIDTH + 15}px;
    gap: 2px;
  }
`;

const InspirationImage = styled.img`
  width: ${INVITE_WIDTH}px;
  max-height: ${INVITE_WIDTH}px;
  object-fit: contain;
  vertical-align: top;
  border-radius: 10px;
  opacity: 1;

  @media (${DeviceQueries.mobile}) {
    width: ${INVITE_MOBILE_WIDTH}px;
    max-height: ${INVITE_MOBILE_WIDTH}px;
  }
`;

const CategoriesSwitch = styled.div`
  display: flex;
  cursor: pointer;
  
  @media not (${DeviceQueries.mobile}) {
    display: none;
  }
`;

const CategoriesContainer = styled.div`
  margin-top: 15px;
`;

const Creator = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 2px;

  font-size: 15px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;

  @media (${DeviceQueries.mobile}) {
    font-size: 12px;
    
    svg {
      width: 15px;
      height: 15px;
    }
  }
`;

export default InstantInvitesPage;
